import { DataService } from "@/services/data";
import { PaginatedResponse } from "@/types/common";
import { ProcessMonthObjective, UserProcess } from "@/types/user/process-objectives";
import { resolveProcessObjectivesForMonth } from "@/pages/settings/targets/process-objectives.resolver";
import { format } from "date-fns";
import { RouteObject } from "react-router-dom";
import { handle_error, handle_success } from "@/utils/wretch";

const TargetsSettingsRoute: RouteObject[] = [
    {
        path: "targets/objectives",
        async lazy() {
            const { TargetsObjectivesPage } = await import("@/pages/settings/targets/objectives");
            return { Component: TargetsObjectivesPage };
        },
        loader: async () => {
            return null;
        },
    },
    {
        path: "targets/payplans",
        async lazy() {
            const { TargetsPayplansPage } = await import("@/pages/settings/targets/payplans");
            return { Component: TargetsPayplansPage };
        },
        loader: async () => {
            return null;
        },
    },
    {
        path: "targets/simulator",
        async lazy() {
            const { TargetsSimulatorPage } = await import("@/pages/settings/targets/simulator");
            return { Component: TargetsSimulatorPage };
        },
        loader: async () => {
            return null;
        },
    },
    {
        path: "targets/process-objectives",
        async lazy() {
            const { ProcessObjectivesPage } = await import("@/pages/settings/targets/process-objectives");
            return { Component: ProcessObjectivesPage };
        },
        loader: async ({ request }) => {
            const url = new URL(request.url);
            const month = url.searchParams.get("month") || format(new Date(), "yyyy-MM");

            // Fetch the full history in effect up to the displayed month (month_lte)
            // so carry-forward can be resolved here. We deliberately do NOT filter by
            // user_process at the API level — the process filter is a UI concern applied
            // after resolution, otherwise we'd lose the rows needed to carry values forward.
            const params = new URLSearchParams();
            params.set("page_size", "1000");
            params.set("month_lte", month + "-01");

            const [objectives, userProcesses] = await Promise.all([
                DataService.client
                    .url(`/users/process-month-objectives/?${params.toString()}`)
                    .get()
                    .json() as Promise<PaginatedResponse<ProcessMonthObjective>>,
                DataService.client.url("/users/process-month-objectives/user_processes/").get().json() as Promise<UserProcess[]>,
            ]);

            const processes = userProcesses ?? [];

            return {
                resolvedObjectives: resolveProcessObjectivesForMonth(objectives?.results ?? [], processes, month),
                userProcesses: processes,
                currentMonth: month,
            };
        },
        action: async ({ request }) => {
            const url = new URL(request.url);
            const action = url.searchParams.get("action");
            const id = url.searchParams.get("id");

            try {
                if (action === "create") {
                    const data = await request.json();
                    const result = await DataService.client.url("/users/process-month-objectives/").post(data).json();
                    return handle_success(result);
                }

                if (action === "update" && id) {
                    const data = await request.json();
                    const result = await DataService.client.url(`/users/process-month-objectives/${id}/`).patch(data).json();
                    return handle_success(result);
                }

                if (action === "delete" && id) {
                    await DataService.client.url(`/users/process-month-objectives/${id}/`).delete().res();
                    return handle_success({});
                }

                return handle_error({ response: { status: 400, statusText: "Bad Request" } });
            } catch (error) {
                return handle_error(error);
            }
        },
    },
];

export default TargetsSettingsRoute;
